import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from 'theme/withStyles';
import { Collapse } from 'react-collapse';
import classnames from 'classnames';
import RawHtml from 'common/RawHtml';
import Spinner from 'common/Spinner';
import { HOME_ROOT } from 'config/api';
import Button from 'common/Button';
import PlusMinus from 'common/PlusMinus';
import homeActions from 'core/home/actions';

import buttonLinkStyles from 'styles/buttonLink';
import commonStyles from './styles';

const styles = (theme) => {
  const common = commonStyles(theme);

  return {
    ...common,
    buttonLink: {
      ...buttonLinkStyles(theme),
      '&:hover': {},
      width: '100%',
    },
    fileUploadLink: {
      ...buttonLinkStyles(theme),
      '&:hover': {
        background: 'none',
        border: 'none',
        color: theme.links.link?.hoverColor,
        textDecoration: 'underline',
      },
      float: 'right',
    },
    hidden: {
      display: 'none',
    },
  };
};

export const HomeDocumentsPanelBare = ({ classes, openDialog, request, resources }) => {
  const { data, isOpen } = useSelector(({ home }) => ({
    data:
      (home[request.id] && home[request.id].documents && home[request.id].documents.data) || null,
    isOpen:
      (home[request.id] && home[request.id].documents && home[request.id].documents.isOpen) ||
      false,
  }));
  const dispatch = useDispatch();

  const panelBody = useMemo(() => {
    if (data === null || data === undefined) {
      return <Spinner loading />;
    }

    if (!data.documentList || data.documentList.length === 0) {
      return <p>This application currently has no documents.</p>;
    }

    return (
      <ul>
        {(data.documentList || []).map((document) => (
          <li
            key={document.id}
            data-name={document.documentName}
            data-id={document.id}
            data-status={document.status}
          >
            {document.id ? (
              /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
              <button
                type="button"
                className={classnames('buttonLink name', classes.buttonLink)}
                onClick={(e) => {
                  e.preventDefault();
                  openDialog('home_documents', {
                    document,
                    request,
                  });
                }}
              >
                {document.documentName}
              </button>
            ) : (
              <span className="name">{document.documentName}</span>
            )}
            <span
              className={classnames('status', classes.status, {
                'accepted-at': document.acceptedAt,
              })}
            >
              <span className="status-label">{document.status}</span>
              {document.uploadedAt && (
                <span className="status-datetime">
                  {': '}
                  {document.uploadedAt}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    );
  }, [classes.buttonLink, classes.status, data, openDialog, request]);

  return (
    <div
      role="tab"
      className={classnames('panel panel-primary loading panel-documents', classes.root)}
    >
      <div className="panel-heading" id={`collapseHeading-documents-${request.id}`}>
        <div
          role="button"
          tabIndex="0"
          aria-expanded={isOpen}
          aria-controls={`collapseAreaID-documents-${request.id}`}
          className={classnames('panel-title', classes.panelTitle)}
          onClick={(e) => {
            e.preventDefault();
            dispatch(homeActions.fetch(request.id, 'documents'));
          }}
          onKeyUp={(e) =>
            (e.keyCode === 13 || e.keyCode === 32) &&
            dispatch(homeActions.fetch(request.id, 'documents'))
          }
        >
          <div
            data-toggle="collapse"
            href={`#collapseHeading-documents-${request.id}`}
            className={classnames('buttonLink get-documents', classes.buttonLink, {
              collapsed: !isOpen,
              [classes.collapsed]: !isOpen,
            })}
          >
            <PlusMinus collapsed={!isOpen} />{' '}
            <RawHtml type="span">{resources.documentsTitle}</RawHtml>{' '}
            {request.documentCategoryTypes &&
              Object.keys(request.documentCategoryTypes).length > 0 && (
                <Button
                  className={classnames('file-upload-link', classes.fileUploadLink)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openDialog('home_document_upload', {
                      appRequest: request,
                      categories: request.documentCategoryTypes,
                      url: `${HOME_ROOT}/${request.id}/documents`,
                    });
                  }}
                >
                  {resources.uploadDocsLinkLabel}{' '}
                  <i
                    className={classnames('icon-arrow-thin-right', classes.iconArrowThinRight)}
                    aria-hidden="true"
                  />
                </Button>
              )}
          </div>
        </div>
      </div>
      <Collapse isOpened={isOpen}>
        <div id={`collapseAreaID-documents-${request.id}`} className="panel-collapse">
          <div
            className={classnames('panel-body', classes.panelBody, {
              [classes.hidden]: !isOpen,
            })}
          >
            {panelBody}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

HomeDocumentsPanelBare.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  openDialog: PropTypes.func.isRequired,
  request: PropTypes.shape({
    documentCategoryTypes: PropTypes.shape({}),
    id: PropTypes.number,
  }).isRequired,
  resources: PropTypes.shape({
    documentsTitle: PropTypes.string,
    uploadDocsLinkLabel: PropTypes.string,
  }).isRequired,
};

const HomeDocumentsPanelStyled = withStyles(styles)(HomeDocumentsPanelBare);

export default HomeDocumentsPanelStyled;
